import type { NativeVideoSettings, NativeVideoSource } from '../NativeVideoProtocol';
import type { PipeStats } from './MediaPipeProtocol';

/** Reserve 2 Mbps inside the existing video cap while both video paths are active. */
export class NativeBootstrapBudget {
  private rates = new Map<RTCRtpSender, Array<number | undefined>>();
  async reserve(peer: RTCPeerConnection | null): Promise<void> {
    const senders = peer?.getSenders().filter(sender => sender.track?.kind === 'video') ?? [];
    for (const sender of senders) {
      const parameters = sender.getParameters() as RTCRtpSendParameters;
      if (!parameters.encodings?.length) throw new Error('Video sender is not negotiated');
      const previous = parameters.encodings.map(encoding => encoding.maxBitrate);
      const total = previous.reduce<number>((sum, rate) => sum + (rate ?? 8000000 / senders.length / previous.length), 0);
      const reserve = 2000000 / senders.length;
      if (total <= reserve) throw new Error('Insufficient video budget for WebCodecs startup');
      parameters.encodings.forEach(encoding => { encoding.maxBitrate = Math.floor((total - reserve) / previous.length); });
      await sender.setParameters(parameters); this.rates.set(sender, previous);
    }
  }
  async restore(): Promise<void> {
    for (const [sender, previous] of this.rates) {
      const parameters = sender.getParameters() as RTCRtpSendParameters;
      parameters.encodings.forEach((encoding, index) => {
        if (previous[index] === undefined) delete encoding.maxBitrate; else encoding.maxBitrate = previous[index];
      });
      await sender.setParameters(parameters); this.rates.delete(sender);
    }
  }
}

/** `stream` only when settings ask for it, or the player asks and settings do not forbid it. */
export function selectSource(settings: NativeVideoSettings, player: { prefersStream: boolean }): NativeVideoSource {
  if (settings.source === 'shadowcam') return 'shadowcam';
  if (settings.source === 'stream' || player.prefersStream) return 'stream';
  return 'shadowcam';
}

export interface DirectStreamPort {
  open(stream: { streamId: number; codec: 'h264' | 'hevc' | 'av1'; playback: 'rtp' | 'webcodecs'; token: Uint8Array; camera?: string; width?: number; height?: number; fps?: number }): void;
  acceptOffer(sdp: string, iceServers: RTCIceServer[]): Promise<string>;
  addIceCandidate(candidate: string): void;
  onLocalCandidate(cb: (candidate: string) => void): void;
  on(event: 'error', listener: (code: string) => void): void;
  stats(): PipeStats | null;
  close(): void;
}

type Clock = {
  setTimeout(handler: () => void, timeout: number): ReturnType<typeof setTimeout>;
  clearTimeout(timer: ReturnType<typeof setTimeout> | undefined): void;
};

/** Signaling for one streaming cam. Frames stay on the transport; this only forwards offers, ICE, and stats. */
export class DirectStreamDriver {
  private timer: ReturnType<typeof setTimeout> | undefined;
  constructor(
    private readonly media: DirectStreamPort,
    private readonly clock: Clock,
    private readonly send: (message: { type: 'answer'; sdp: string } | { type: 'ice'; candidate: RTCIceCandidateInit }) => void,
    private readonly publish: (stats: PipeStats) => void,
    unavailable: () => void,
  ) {
    media.on('error', (code) => { if (code === 'gpu-path-unavailable') unavailable(); });
  }

  open(stream: { streamId: number; codec: 'h264' | 'hevc' | 'av1'; playback: 'rtp' | 'webcodecs'; token: Uint8Array; camera?: string; width?: number; height?: number; fps?: number }): void {
    this.media.open(stream);
    const poll = () => {
      const stats = this.media.stats();
      if (stats) this.publish(stats);
      this.timer = this.clock.setTimeout(poll, 1000);
    };
    this.timer = this.clock.setTimeout(poll, 1000);
  }

  async accept(sdp: string, iceServers: RTCIceServer[]): Promise<void> {
    this.media.onLocalCandidate((candidate) => {
      this.send({ type: 'ice', candidate: { candidate, sdpMid: '0', sdpMLineIndex: 0 } });
    });
    const answer = await this.media.acceptOffer(sdp, iceServers);
    this.send({ type: 'answer', sdp: answer });
  }

  addIce(candidate: string): void { this.media.addIceCandidate(candidate); }

  close(): void { this.clock.clearTimeout(this.timer); this.timer = undefined; this.media.close(); }
}
